import { useState } from 'react';
import {
  FileInput,
  Plus,
  Pencil,
  Trash2,
  X,
  GripVertical,
  Copy,
  ArrowLeft,
  BarChart3,
  Check,
} from 'lucide-react';
import {
  useWebForms,
  useCreateWebForm,
  useUpdateWebForm,
  useDeleteWebForm,
  useFormAnalytics,
  type WebForm,
  type WebFormField,
  type WebFormFieldType,
  type WebFormSubmitAction,
} from '../hooks/useWebForms';
import { Badge } from '../components/shared/Badge';
import { EmptyState } from '../components/shared/EmptyState';
import { cn } from '../lib/cn';

interface WebFormsViewProps {
  onNavigate: (view: string, params?: Record<string, string>) => void;
}

const fieldTypeOptions: { value: WebFormFieldType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'textarea', label: 'Textarea' },
  { value: 'select', label: 'Select' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'date', label: 'Date' },
  { value: 'number', label: 'Number' },
  { value: 'hidden', label: 'Hidden' },
];

const submitActionOptions: { value: WebFormSubmitAction; label: string }[] = [
  { value: 'create_contact', label: 'Create Contact' },
  { value: 'create_lead', label: 'Create Lead' },
  { value: 'create_deal', label: 'Create Deal' },
  { value: 'custom', label: 'Custom (webhook)' },
];

type ViewMode = 'list' | 'form' | 'embed' | 'analytics';

// --- Form Builder ---

interface FormBuilderProps {
  initial?: WebForm;
  onSave: (data: Partial<WebForm>) => void;
  onCancel: () => void;
  isSaving: boolean;
}

function FormBuilder({ initial, onSave, onCancel, isSaving }: FormBuilderProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [submitAction, setSubmitAction] = useState<WebFormSubmitAction>(initial?.submitAction ?? 'create_lead');
  const [redirectUrl, setRedirectUrl] = useState(initial?.redirectUrl ?? '');
  const [successMessage, setSuccessMessage] = useState(initial?.successMessage ?? 'Thank you for your submission!');
  const [buttonText, setButtonText] = useState(initial?.styling?.buttonText ?? 'Submit');
  const [fields, setFields] = useState<WebFormField[]>(
    initial?.fields ?? [
      { id: '1', type: 'text', label: 'Name', required: true, order: 0 },
      { id: '2', type: 'email', label: 'Email', required: true, order: 1 },
    ],
  );

  function addField() {
    const id = String(Date.now());
    setFields([...fields, { id, type: 'text', label: '', required: false, order: fields.length }]);
  }

  function updateField(index: number, patch: Partial<WebFormField>) {
    setFields(fields.map((f, i) => (i === index ? { ...f, ...patch } : f)));
  }

  function removeField(index: number) {
    setFields(fields.filter((_, i) => i !== index).map((f, i) => ({ ...f, order: i })));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSave({
      name,
      description: description || undefined,
      fields,
      submitAction,
      redirectUrl: redirectUrl || undefined,
      successMessage,
      styling: { buttonText },
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 p-6">
      <div className="card p-5 space-y-4">
        <h3 className="text-sm font-semibold text-slate-900">Form Settings</h3>
        <div>
          <label className="label">Form Name</label>
          <input
            type="text"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Contact Us"
            required
          />
        </div>
        <div>
          <label className="label">Description</label>
          <input
            type="text"
            className="input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Submit Action</label>
            <select
              className="input"
              value={submitAction}
              onChange={(e) => setSubmitAction(e.target.value as WebFormSubmitAction)}
            >
              {submitActionOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Button Text</label>
            <input
              type="text"
              className="input"
              value={buttonText}
              onChange={(e) => setButtonText(e.target.value)}
              placeholder="Submit"
            />
          </div>
        </div>
        <div>
          <label className="label">Redirect URL (optional)</label>
          <input
            type="text"
            className="input"
            value={redirectUrl}
            onChange={(e) => setRedirectUrl(e.target.value)}
            placeholder="https://example.com/thank-you"
          />
        </div>
        <div>
          <label className="label">Success Message</label>
          <input
            type="text"
            className="input"
            value={successMessage}
            onChange={(e) => setSuccessMessage(e.target.value)}
            placeholder="Thank you for your submission!"
          />
        </div>
      </div>

      <div className="card p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900">Fields</h3>
          <button type="button" onClick={addField} className="btn-secondary btn-sm">
            <Plus className="h-3.5 w-3.5" />
            Add Field
          </button>
        </div>

        <div className="space-y-2">
          {fields.map((field, i) => (
            <div key={field.id} className="flex items-center gap-2 rounded-lg border border-surface-200 p-3">
              <GripVertical className="h-4 w-4 shrink-0 text-slate-300" />
              <input
                type="text"
                className="input flex-1"
                value={field.label}
                onChange={(e) => updateField(i, { label: e.target.value })}
                placeholder="Label"
              />
              <select
                className="input w-32"
                value={field.type}
                onChange={(e) => updateField(i, { type: e.target.value as WebFormFieldType })}
              >
                {fieldTypeOptions.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              <input
                type="text"
                className="input w-36"
                value={field.placeholder ?? ''}
                onChange={(e) => updateField(i, { placeholder: e.target.value || undefined })}
                placeholder="Placeholder"
              />
              <label className="flex items-center gap-1 text-xs text-slate-500 whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={field.required}
                  onChange={(e) => updateField(i, { required: e.target.checked })}
                  className="h-3.5 w-3.5 rounded border-surface-300"
                />
                Required
              </label>
              <button
                type="button"
                onClick={() => removeField(i)}
                className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-500"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-end gap-3">
        <button type="button" onClick={onCancel} className="btn-secondary">Cancel</button>
        <button type="submit" className="btn-primary" disabled={isSaving || !name.trim()}>
          {isSaving ? 'Saving...' : initial ? 'Update Form' : 'Create Form'}
        </button>
      </div>
    </form>
  );
}

// --- Embed Code Panel ---

function EmbedCodePanel({ formId, onBack }: { formId: string; onBack: () => void }) {
  const [copied, setCopied] = useState(false);
  const embedCode = `<iframe src="${window.location.origin}/forms/${formId}" style="width:100%;min-height:400px;border:none;" title="Web Form"></iframe>`;

  function handleCopy() {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="p-6 space-y-4">
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to forms
      </button>

      <div className="card p-5 space-y-3">
        <h3 className="text-sm font-semibold text-slate-900">Embed Code</h3>
        <p className="text-xs text-slate-500">Copy and paste this HTML snippet into your website.</p>
        <div className="relative">
          <pre className="rounded-lg bg-slate-900 p-4 text-xs text-slate-100 overflow-x-auto">
            {embedCode}
          </pre>
          <button
            onClick={handleCopy}
            className="absolute top-2 right-2 rounded bg-slate-700 px-2 py-1 text-xs text-slate-200 hover:bg-slate-600"
          >
            {copied ? <Check className="h-3.5 w-3.5 inline" /> : <Copy className="h-3.5 w-3.5 inline" />}
            {copied ? ' Copied' : ' Copy'}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Analytics Panel ---

function AnalyticsPanel({ formId, onBack }: { formId: string; onBack: () => void }) {
  const { data, isLoading } = useFormAnalytics(formId);

  return (
    <div className="p-6 space-y-4">
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to forms
      </button>

      {isLoading ? (
        <div className="grid grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="card p-5">
              <div className="h-6 w-16 animate-pulse rounded bg-surface-200" />
            </div>
          ))}
        </div>
      ) : data ? (
        <>
          <div className="grid grid-cols-4 gap-4">
            <div className="card p-5 text-center">
              <div className="text-2xl font-bold text-slate-900">{data.totalSubmissions}</div>
              <div className="text-xs text-slate-500">Total Submissions</div>
            </div>
            <div className="card p-5 text-center">
              <div className="text-2xl font-bold text-slate-900">{data.submissionsThisWeek}</div>
              <div className="text-xs text-slate-500">This Week</div>
            </div>
            <div className="card p-5 text-center">
              <div className="text-2xl font-bold text-slate-900">{data.submissionsThisMonth}</div>
              <div className="text-xs text-slate-500">This Month</div>
            </div>
            <div className="card p-5 text-center">
              <div className="text-2xl font-bold text-slate-900">{data.conversionRate}%</div>
              <div className="text-xs text-slate-500">Conversion Rate</div>
            </div>
          </div>

          {data.topReferrers.length > 0 && (
            <div className="card p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Top Referrers</h3>
              <div className="space-y-2">
                {data.topReferrers.map((r) => (
                  <div key={r.url} className="flex items-center justify-between text-sm">
                    <span className="text-slate-600 truncate">{r.url}</span>
                    <span className="text-slate-900 font-medium">{r.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

// --- Main View ---

export function WebFormsView({ onNavigate }: WebFormsViewProps) {
  const [mode, setMode] = useState<ViewMode>('list');
  const [editingForm, setEditingForm] = useState<WebForm | null>(null);
  const [selectedFormId, setSelectedFormId] = useState<string | null>(null);

  const { data, isLoading, error } = useWebForms();
  const createForm = useCreateWebForm();
  const updateForm = useUpdateWebForm();
  const deleteForm = useDeleteWebForm();

  const forms = data?.forms ?? [];

  function handleCreate() {
    setEditingForm(null);
    setMode('form');
  }

  function handleEdit(form: WebForm) {
    setEditingForm(form);
    setMode('form');
  }

  function handleSave(formData: Partial<WebForm>) {
    if (editingForm) {
      updateForm.mutate(
        { id: editingForm.id, data: formData },
        { onSuccess: () => setMode('list') },
      );
    } else {
      createForm.mutate(formData, { onSuccess: () => setMode('list') });
    }
  }

  function handleEmbed(formId: string) {
    setSelectedFormId(formId);
    setMode('embed');
  }

  function handleAnalytics(formId: string) {
    setSelectedFormId(formId);
    setMode('analytics');
  }

  function handleDelete(id: string) {
    deleteForm.mutate(id);
  }

  // --- Embed Mode ---
  if (mode === 'embed' && selectedFormId) {
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="page-header">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Embed Code</h1>
            <p className="mt-0.5 text-sm text-slate-500">Copy the embed snippet</p>
          </div>
        </div>
        <EmbedCodePanel formId={selectedFormId} onBack={() => setMode('list')} />
      </div>
    );
  }

  // --- Analytics Mode ---
  if (mode === 'analytics' && selectedFormId) {
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="page-header">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Form Analytics</h1>
            <p className="mt-0.5 text-sm text-slate-500">Submission metrics</p>
          </div>
        </div>
        <AnalyticsPanel formId={selectedFormId} onBack={() => setMode('list')} />
      </div>
    );
  }

  // --- Form Mode ---
  if (mode === 'form') {
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="page-header">
          <div>
            <h1 className="text-xl font-bold text-slate-900">
              {editingForm ? 'Edit Form' : 'New Web Form'}
            </h1>
            <p className="mt-0.5 text-sm text-slate-500">
              {editingForm ? 'Update form configuration' : 'Build a new lead capture form'}
            </p>
          </div>
        </div>
        <FormBuilder
          initial={editingForm ?? undefined}
          onSave={handleSave}
          onCancel={() => setMode('list')}
          isSaving={createForm.isPending || updateForm.isPending}
        />
      </div>
    );
  }

  // --- List Mode ---
  return (
    <div className="flex-1 overflow-y-auto">
      <div className="page-header">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Web Forms</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {data ? `${data.total} form${data.total !== 1 ? 's' : ''}` : 'Lead capture forms'}
          </p>
        </div>
        <button className="btn-primary" onClick={handleCreate}>
          <Plus className="h-4 w-4" />
          New Form
        </button>
      </div>

      <div className="p-6">
        {error ? (
          <div className="card p-8 text-center text-sm text-red-600">
            Failed to load web forms. Please try again.
          </div>
        ) : isLoading ? (
          <div className="card divide-y divide-surface-100">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-4 py-4">
                <div className="h-4 w-40 animate-pulse rounded bg-surface-200" />
                <div className="h-4 w-24 animate-pulse rounded bg-surface-100" />
              </div>
            ))}
          </div>
        ) : forms.length > 0 ? (
          <div className="card divide-y divide-surface-100">
            <div className="grid grid-cols-12 gap-4 px-4 py-2 text-xs font-medium text-slate-500 bg-surface-50">
              <div className="col-span-3">Name</div>
              <div className="col-span-2">Submissions</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2">Last Submission</div>
              <div className="col-span-3 text-right">Actions</div>
            </div>
            {forms.map((form) => (
              <div key={form.id} className="grid grid-cols-12 gap-4 items-center px-4 py-3">
                <div className="col-span-3">
                  <div className="text-sm font-medium text-slate-900">{form.name}</div>
                  {form.description && (
                    <div className="text-xs text-slate-400 truncate">{form.description}</div>
                  )}
                </div>
                <div className="col-span-2 text-sm text-slate-600">{form.submissionCount}</div>
                <div className="col-span-2">
                  <Badge color={form.isActive ? 'green' : 'gray'} size="sm">
                    {form.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <div className="col-span-2 text-xs text-slate-400">
                  {form.lastSubmissionAt
                    ? new Date(form.lastSubmissionAt).toLocaleDateString()
                    : 'Never'}
                </div>
                <div className="col-span-3 flex items-center justify-end gap-1">
                  <button
                    onClick={() => handleAnalytics(form.id)}
                    className="rounded p-1.5 text-slate-400 hover:bg-surface-100 hover:text-slate-600"
                    title="Analytics"
                  >
                    <BarChart3 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleEmbed(form.id)}
                    className="rounded p-1.5 text-slate-400 hover:bg-surface-100 hover:text-slate-600"
                    title="Embed code"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleEdit(form)}
                    className="rounded p-1.5 text-slate-400 hover:bg-surface-100 hover:text-slate-600"
                    title="Edit"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(form.id)}
                    className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-500"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={FileInput}
            title="No web forms"
            description="Create a form to capture leads from your website."
            actionLabel="Create Form"
            onAction={handleCreate}
          />
        )}
      </div>
    </div>
  );
}
